import {
  Button,
  formatMoney,
  type LngLat,
  type MapPin,
  type MapPoint,
  PageError,
  PageHeader,
  PageLoading,
  TextButton,
  ZoneMap,
} from "@majitap/ui";
import { Alert } from "@majitap/ui/arc/alert/alert";
import { Badge } from "@majitap/ui/arc/badge/badge";
import { Dialog, DialogContent } from "@majitap/ui/arc/dialog/dialog";
import { HoldToConfirm } from "@majitap/ui/arc/hold-to-confirm/hold-to-confirm";
import { Input } from "@majitap/ui/arc/input/input";
import SegmentedControl from "@majitap/ui/arc/segmented-control/segmented-control";
import { Select } from "@majitap/ui/arc/select/select";
import { Switch } from "@majitap/ui/arc/switch/switch";
import { Textarea } from "@majitap/ui/arc/textarea/textarea";
import { useMutation, useQuery, useQueryClient, useSuspenseQuery } from "@tanstack/react-query";
import { useNavigate, useSearch } from "@tanstack/react-router";
import { MapPinOff, Trash2, X } from "lucide-react";
import { type ReactNode, type RefObject, useLayoutEffect, useMemo, useRef, useState } from "react";
import { errorOf } from "../../api";
import { useCompanyFormat } from "../../company";
import { messageFor } from "../../error-copy";
import { filterSearch } from "../../list-search";
import { RouterLink } from "../../router-link";
import { meQuery } from "../../session";
import {
  allZoneLocations,
  breakdown,
  cellOf,
  geometryFromGeoJson,
  type LookedAt,
  movedSummary,
  movedText,
  outlineOf,
  outsidePlacesQuery,
  polygonOf,
  shapeEditable,
  toMapZone,
  type Zone,
  type ZoneLocation,
  type ZoneSheet,
  zoneActions,
  zoneAtQuery,
  zoneLocationsQuery,
  zoneSheetQuery,
  zonesListQuery,
} from "../../zones";
import { ConfirmDialog } from "../shared/confirm";
import { ZonesTable } from "./list";
import { PlacePanel } from "./place-panel";
import { PlaceSearch } from "./place-search";
import { PriceBreakdown, ZonePriceList } from "./prices";
import styles from "./zones.module.css";

export const zoneFilters = filterSearch({
  zone: "text",
  view: ["map", "list"],
  item: "text",
  status: ["active", "off"],
});

/** The zones a status filter shows; the open zone always stays. */
export function zonesShown<Z extends Pick<Zone, "id" | "status">>(
  zones: Z[],
  status: string | undefined,
  openId?: string,
) {
  if (status !== "active" && status !== "off") return zones;
  return zones.filter((z) => z.id === openId || (status === "active") === (z.status === "active"));
}

/** The item prices are shown for: the one chosen, else the first truck load, else the first. */
export function defaultItem(sheet: Pick<ZoneSheet, "items"> | undefined, chosen: string | undefined) {
  const items = sheet?.items ?? [];
  if (chosen && items.some((i) => i.id === chosen)) return chosen;
  return (items.find((i) => i.fulfillment === "truck_load") ?? items[0])?.id;
}

/** How far around a looked-up place the map shows, in degrees (about 3 km in Nairobi). */
const PLACE_VIEW_DEG = 0.015;

/** The map's least height, in pixels: on a short window the page scrolls instead of squeezing it. */
const MIN_MAP_PX = 448;

/**
 * The height left for the map and its panel: from where they start to the bottom of the page's
 * scrolling area, less its padding, so the page fits the window as the design does. Measured after
 * every render (what sits above them changes: an alert, the toolbar) and on resize.
 */
export function useFillHeight(ref: RefObject<HTMLElement | null>): number {
  const [height, setHeight] = useState(640);
  useLayoutEffect(() => {
    const measure = () => {
      const el = ref.current;
      if (!el) return;
      let scroller: HTMLElement | null = el.parentElement;
      while (scroller && !/(auto|scroll)/.test(getComputedStyle(scroller).overflowY)) scroller = scroller.parentElement;
      const box = (scroller ?? document.documentElement).getBoundingClientRect();
      // number-ok: CSS pixels, not money
      const padding = scroller ? Number.parseFloat(getComputedStyle(scroller).paddingBottom) || 0 : 0;
      const top = el.getBoundingClientRect().top;
      const bottom = scroller ? box.bottom : window.innerHeight;
      // as if scrolled to the top: what fits from the map's top to the bottom of the page
      setHeight(Math.max(MIN_MAP_PX, Math.floor(bottom - padding - top - (scroller?.scrollTop ?? 0))));
    };
    measure();
    window.addEventListener("resize", measure);
    return () => window.removeEventListener("resize", measure);
  });
  return height;
}

/** How long Delete must be held: the zone's places lose it at once. */
export const DELETE_HOLD_MS = 1500;

type Editing = { mode: "new" } | { mode: "shape"; zone: Zone } | null;
type Moved = {
  name: string;
  verb?: "saved" | "deleted" | "switched off" | "switched on";
  relinked: number;
  movedIn: ZoneLocation[];
  movedOut: ZoneLocation[];
} | null;

/**
 * Service zones (P8-21, `Console / Service zones`): the areas MajiTap delivers in, on a map beside
 * the list. Changing a shape re-matches every place in the same save; the page then says who moved.
 *
 * P8-40: as a map or a list, with what each zone costs for the chosen item, and a search that
 * finds a zone, a saved place, an address or a dropped pin, and prices it.
 */
export function ZonesPage(): ReactNode {
  const search = useSearch({ from: "/console/zones" });
  const navigate = useNavigate({ from: "/zones" });
  const { data: me } = useSuspenseQuery(meQuery);
  const format = useCompanyFormat();
  const canEdit = me?.permissions.includes("pricing.manage") ?? false;
  const canPrice = me?.permissions.includes("orders.read") ?? false;
  const zones = useQuery(zonesListQuery);
  const sheet = useQuery({ ...zoneSheetQuery, enabled: canPrice });
  const view = search.view ?? "map";
  const itemId = defaultItem(sheet.data, search.item);
  const selected = zones.data?.find((z) => z.id === search.zone) ?? null;
  const locations = useQuery({ ...zoneLocationsQuery(selected?.id ?? ""), enabled: Boolean(selected) });
  const [editing, setEditing] = useState<Editing>(null);
  const [draft, setDraft] = useState<LngLat[] | null>(null);
  const [moved, setMoved] = useState<Moved>(null);
  const [dialog, setDialog] = useState<"rename" | "status" | "delete" | null>(null);
  const [place, setPlace] = useState<LookedAt | null>(null);
  const [dropping, setDropping] = useState(false);
  const outside = useQuery(outsidePlacesQuery);
  const select = (id: string | undefined) => {
    setPlace(null);
    return navigate({ search: (s) => ({ ...s, zone: id }), replace: true });
  };
  const shown = useMemo(
    () => zonesShown(zones.data ?? [], search.status, search.zone),
    [zones.data, search.status, search.zone],
  );

  const mapZones = useMemo(
    () =>
      shown
        .filter((z) => !(editing?.mode === "shape" && editing.zone.id === z.id))
        .map((z) => {
          const cell = cellOf(sheet.data, z.id, itemId);
          const price = cell && !cell.missing ? breakdown(cell.lines).price : null;
          const note =
            z.status === "inactive"
              ? "Off"
              : price
                ? `${formatMoney(price.amount, format)}${price.scope === "zone" ? "" : " (any zone)"}`
                : cell?.missing
                  ? "No price"
                  : undefined;
          return { ...toMapZone(z), note };
        }),
    [shown, editing, sheet.data, itemId, format],
  );
  // the place's zone (the same query as its panel, so one request), for the pin's price or its red
  const placeZone = useQuery({ ...zoneAtQuery(place?.lng ?? 0, place?.lat ?? 0), enabled: Boolean(place) });
  const pin = useMemo<MapPin | null>(() => {
    if (!place) return null;
    const zone = placeZone.data?.geofence;
    if (placeZone.data && !zone)
      return { lng: place.lng, lat: place.lat, label: place.label, note: "Not priced", tone: "danger" };
    const cell = zone ? cellOf(sheet.data, zone.id, itemId) : null;
    const item = sheet.data?.items.find((i) => i.id === itemId);
    const note =
      cell?.receivable_total && item ? `${formatMoney(cell.receivable_total, format)} / ${item.name}` : undefined;
    return { lng: place.lng, lat: place.lat, label: place.label, note };
  }, [place, placeZone.data, sheet.data, itemId, format]);
  // the open zone's places, and in red the places no zone covers (the design's map)
  const points = useMemo<MapPoint[]>(
    () => [
      ...(selected ? (locations.data ?? []) : [])
        .filter((l) => l.longitude !== null && l.latitude !== null)
        .map((l) => ({ id: l.id, lng: l.longitude as number, lat: l.latitude as number, label: l.label })),
      ...(outside.data ?? [])
        .filter((l) => l.longitude !== null && l.latitude !== null)
        .map((l) => ({
          id: `outside-${l.id}`,
          lng: l.longitude as number,
          lat: l.latitude as number,
          label: l.label,
          tone: "danger" as const,
        })),
    ],
    [selected, locations.data, outside.data],
  );
  const outsideCount = outside.data?.length ?? 0;
  // a place: a few kilometres around it, so its zone's edges show, not the street at full zoom
  const fit = useRef<HTMLDivElement>(null);
  const fitHeight = useFillHeight(fit);
  const focus = useMemo<LngLat[] | null>(
    () =>
      place
        ? [
            [place.lng - PLACE_VIEW_DEG, place.lat - PLACE_VIEW_DEG],
            [place.lng + PLACE_VIEW_DEG, place.lat + PLACE_VIEW_DEG],
          ]
        : selected
          ? outlineOf(selected)
          : null,
    [place, selected],
  );

  if (zones.status === "pending") return <PageLoading label="Loading service zones" />;
  if (zones.status === "error")
    return <PageError message={messageFor(errorOf(zones.error))} onRetry={() => zones.refetch()} />;

  const startNew = () => {
    setMoved(null);
    setEditing({ mode: "new" });
    setDraft([]);
  };
  const startShape = (z: Zone) => {
    setMoved(null);
    setEditing({ mode: "shape", zone: z });
    setDraft(outlineOf(z));
  };
  const stop = () => {
    setEditing(null);
    setDraft(null);
  };

  return (
    <>
      <PageHeader
        title="Service zones"
        description="Where you deliver and what it costs there. A zone's own price wins over the any-zone price; a place outside every zone can't be priced or ordered to."
        actions={canEdit && !editing ? <Button onClick={startNew}>New zone</Button> : undefined}
      />
      {moved ? <MovedSummary moved={moved} onClose={() => setMoved(null)} /> : null}
      {editing ? null : (
        <div className={styles.toolbar}>
          <SegmentedControl
            label="Show as"
            value={view}
            onValueChange={(v) => navigate({ search: (s) => ({ ...s, view: v === "list" ? "list" : undefined }) })}
            options={[
              { value: "map", label: "Map" },
              { value: "list", label: "List" },
            ]}
          />
          <div className={`${styles.quietLabel} ${styles.searchSlot}`}>
            <PlaceSearch
              zones={zones.data}
              chosen={place?.label ?? null}
              onClear={() => setPlace(null)}
              onZone={(id) => {
                setPlace(null);
                void navigate({ search: (s) => ({ ...s, zone: id, view: undefined }) });
              }}
              onPlace={(p) => {
                setDropping(false);
                setPlace(p);
                void navigate({ search: (s) => ({ ...s, zone: undefined, view: undefined }) });
              }}
              onDropPin={() => {
                setDropping(true);
                void navigate({ search: (s) => ({ ...s, view: undefined }) });
              }}
            />
          </div>
          <SegmentedControl
            label="Zones shown"
            value={search.status ?? "all"}
            onValueChange={(v) =>
              navigate({ search: (s) => ({ ...s, status: v === "active" || v === "off" ? v : undefined }) })
            }
            options={[
              { value: "all", label: `All ${zones.data.length}` },
              { value: "active", label: `Active ${zones.data.filter((z) => z.status === "active").length}` },
              { value: "off", label: `Off ${zones.data.filter((z) => z.status !== "active").length}` },
            ]}
          />
          <span className={styles.toolbarGap} />
          {outsideCount ? (
            <button
              type="button"
              className={styles.outsideChip}
              onClick={() => {
                setPlace(null);
                void navigate({ search: (s) => ({ ...s, zone: undefined, view: undefined }) });
              }}
            >
              <MapPinOff size={16} aria-hidden />
              {outsideCount >= 50 ? "50 or more places" : outsideCount === 1 ? "1 place" : `${outsideCount} places`}{" "}
              outside every zone
            </button>
          ) : null}
          {sheet.data?.items.length ? (
            <div className={`${styles.quietLabel} ${styles.pricesFor}`}>
              <span aria-hidden>Prices for</span>
              <Select
                label="Prices for"
                value={itemId}
                onValueChange={(v) => navigate({ search: (s) => ({ ...s, item: v }), replace: true })}
                options={sheet.data.items.map((i) => ({ value: i.id, label: i.name }))}
              />
            </div>
          ) : null}
        </div>
      )}
      {sheet.error ? (
        <Alert tone="warning" title="Prices didn't load">
          {messageFor(errorOf(sheet.error))}
        </Alert>
      ) : null}
      {view === "list" && !editing ? (
        <ZonesTable
          zones={shown}
          sheet={canPrice ? (sheet.data ?? null) : null}
          itemId={itemId}
          format={format}
          onShowOnMap={(id) => navigate({ search: (s) => ({ ...s, zone: id, view: undefined }) })}
        />
      ) : (
        <div className={styles.layout} ref={fit}>
          <ZoneMap
            label="Service zones: select a zone's name to open it"
            zones={mapZones}
            points={editing || place ? [] : points}
            selectedId={selected?.id ?? null}
            onSelectZone={dropping ? undefined : (id) => select(id === selected?.id ? undefined : id)}
            onMapClick={
              dropping
                ? ([lng, lat]) => {
                    setDropping(false);
                    setPlace({
                      kind: "pin",
                      label: "Dropped pin",
                      // number-ok: map coordinates, shown to 5 places (about a metre), not money
                      detail: `${lat.toFixed(5)}, ${lng.toFixed(5)}`,
                      lng,
                      lat,
                    });
                    void navigate({ search: (s) => ({ ...s, zone: undefined }), replace: true });
                  }
                : undefined
            }
            draft={draft}
            onDraftChange={setDraft}
            focus={editing ? null : focus}
            zoneLabels={!editing}
            pin={editing ? null : pin}
            fitAll={!editing}
            legend={editing ? undefined : <MapLegend />}
            height={`${fitHeight}px`}
          >
            {editing ? (
              <p className={styles.hint}>
                Click the map to add a corner. Drag a corner to move it; click one to remove it.
              </p>
            ) : dropping ? (
              <p className={styles.hint}>
                Click the map where the place is. <TextButton onClick={() => setDropping(false)}>Cancel</TextButton>
              </p>
            ) : null}
          </ZoneMap>
          <aside className={styles.side} aria-label="Details" style={{ maxHeight: fitHeight }}>
            {place && !editing ? (
              <PlacePanel
                place={place}
                sheet={canPrice ? (sheet.data ?? null) : null}
                itemId={itemId}
                format={format}
                canEdit={canEdit}
                onClose={() => setPlace(null)}
                onOpenZone={(id) => select(id)}
                onNewZone={() => {
                  setPlace(null);
                  startNew();
                }}
              />
            ) : editing ? (
              <ZoneEditor
                editing={editing}
                draft={draft ?? []}
                onCancel={stop}
                onSaved={(result) => {
                  stop();
                  setMoved(result.moved);
                  void select(result.id);
                }}
              />
            ) : selected ? (
              <section className={styles.card} aria-label={selected.name}>
                <div className={styles.cardHead}>
                  <div className={styles.titleRow}>
                    <h2 className={styles.zoneTitle}>{selected.name}</h2>
                    <Badge tone={selected.status === "active" ? "success" : "neutral"}>
                      {selected.status === "active" ? "Active" : "Off"}
                    </Badge>
                  </div>
                  <button
                    type="button"
                    className={styles.iconButton}
                    aria-label="Close, and show every zone"
                    onClick={() => select(undefined)}
                  >
                    <X size={16} aria-hidden />
                  </button>
                </div>
                <dl className={styles.stats}>
                  <div>
                    <dt>Area</dt>
                    <dd>{selected.area_km2 ? `${selected.area_km2} km²` : "—"}</dd>
                  </div>
                  <div>
                    <dt>Places</dt>
                    <dd>{selected.location_count}</dd>
                  </div>
                </dl>
                {canEdit ? (
                  <div className={styles.actions}>
                    {shapeEditable(selected) ? (
                      <TextButton onClick={() => startShape(selected)}>Edit the shape</TextButton>
                    ) : (
                      <TextButton onClick={() => startShape(selected)}>Replace the shape</TextButton>
                    )}
                    <TextButton onClick={() => setDialog("rename")}>Rename</TextButton>
                    <TextButton onClick={() => setDialog("status")}>
                      {selected.status === "active" ? "Switch off" : "Switch on"}
                    </TextButton>
                    <TextButton className={styles.danger} onClick={() => setDialog("delete")}>
                      Delete
                    </TextButton>
                  </div>
                ) : null}
                {canEdit && !shapeEditable(selected) ? (
                  <p className={styles.muted}>
                    This zone has several parts or a hole, so the map can't redraw it without losing the rest. Replace
                    the shape with GeoJSON from a GIS tool instead.
                  </p>
                ) : null}
                {canPrice && sheet.data ? <ZonePrices zone={selected} sheet={sheet.data} itemId={itemId} /> : null}
              </section>
            ) : (
              <>
                <p className={styles.muted}>
                  {zones.data.length === 0
                    ? "No zones yet. Draw the first with New zone."
                    : `${zones.data.length === 1 ? "1 zone" : `${zones.data.length} zones`}. Select a zone's name on the map to see its prices.`}
                </p>
                <OutsidePlaces />
              </>
            )}
          </aside>
        </div>
      )}
      {selected && dialog === "rename" ? <RenameDialog zone={selected} onClose={() => setDialog(null)} /> : null}
      {selected && dialog === "status" ? (
        <StatusDialog zone={selected} onMoved={setMoved} onClose={() => setDialog(null)} />
      ) : null}
      {selected && dialog === "delete" ? (
        <DeleteDialog
          zone={selected}
          onDeleted={(m) => {
            setDialog(null);
            setMoved(m);
            void select(undefined);
          }}
          onClose={() => setDialog(null)}
        />
      ) : null}
    </>
  );
}

function PlaceLink({ place }: { place: Pick<ZoneLocation, "label" | "address_line" | "customer_id" | "business_id"> }) {
  const href = place.business_id
    ? `/businesses/${place.business_id}?tab=places`
    : `/customers/${place.customer_id}?tab=places`;
  return (
    <RouterLink href={href} className={styles.link}>
      {[place.label, place.address_line].filter(Boolean).join(" · ")}
    </RouterLink>
  );
}

/** A zone's prices (P8-40): every item's, then the chosen item's charges, costs and margin. */
function ZonePrices({ zone, sheet, itemId }: { zone: Zone; sheet: ZoneSheet; itemId: string | undefined }) {
  const format = useCompanyFormat();
  const cell = cellOf(sheet, zone.id, itemId);
  return (
    <>
      <section className={styles.section} aria-label="Prices here">
        <h3 className={styles.subtitle}>Prices here</h3>
        <p className={styles.sectionHint}>What a customer in {zone.name} pays today, one of each</p>
        <ZonePriceList sheet={sheet} zoneId={zone.id} zoneName={zone.name} format={format} />
      </section>
      {cell && !cell.missing && cell.receivable_total && cell.payable_total && cell.margin ? (
        <section className={styles.section} aria-label="One delivery">
          <h3 className={styles.subtitle}>{sheet.items.find((i) => i.id === itemId)?.name}, one delivery</h3>
          <p className={styles.sectionHint}>The charges, what MajiTap pays partners, and the margin</p>
          <PriceBreakdown
            lines={cell.lines}
            totals={{ receivable: cell.receivable_total, payable: cell.payable_total, margin: cell.margin }}
            zoneName={zone.name}
            format={format}
          />
        </section>
      ) : null}
      <RouterLink href={`/pricing?geofence_id=${zone.id}`} className={styles.link}>
        Its rules in Pricing →
      </RouterLink>
    </>
  );
}

/** Who moved in and out of a zone after a change: the console's answer to the relink trap. */
export function MovedSummary({ moved, onClose }: { moved: NonNullable<Moved>; onClose?: () => void }) {
  const { movedIn, movedOut } = moved;
  return (
    <div className={styles.summary}>
      <Alert tone={movedOut.length ? "warning" : "success"} title={`${moved.name} ${moved.verb ?? "saved"}`}>
        {movedText(movedIn, movedOut)}
        {movedOut.length
          ? " Places that moved out are now in another zone, or none: check them before customers order."
          : ""}
      </Alert>
      {moved.relinked > movedIn.length + movedOut.length ? (
        <p className={styles.muted}>
          In all, {moved.relinked} places and fill sites changed zone, other zones' and fill sites included. A fill
          site's zone decides its water price.
        </p>
      ) : null}
      {movedOut.length || movedIn.length ? (
        <ul className={styles.places} aria-label="Places that changed zone">
          {movedOut.slice(0, 20).map((l) => (
            <li key={`out-${l.id}`}>
              Out: <PlaceLink place={l} />
            </li>
          ))}
          {movedIn.slice(0, 20).map((l) => (
            <li key={`in-${l.id}`}>
              In: <PlaceLink place={l} />
            </li>
          ))}
        </ul>
      ) : null}
      {onClose ? (
        <div>
          <TextButton onClick={onClose}>Dismiss</TextButton>
        </div>
      ) : null}
    </div>
  );
}

/** Draw a new zone, or reshape one. The save re-matches every place; who moved is shown after. */
function ZoneEditor({
  editing,
  draft,
  onCancel,
  onSaved,
}: {
  editing: NonNullable<Editing>;
  draft: LngLat[];
  onCancel: () => void;
  onSaved: (result: { id: string; moved: NonNullable<Moved> }) => void;
}) {
  const queryClient = useQueryClient();
  const [name, setName] = useState(editing.mode === "shape" ? editing.zone.name : "");
  const [startOff, setStartOff] = useState(false);
  // a shape from a GIS file instead of the map: any number of parts and holes
  const [geojson, setGeojson] = useState("");
  const pasted = geojson.trim() ? geometryFromGeoJson(geojson) : null;
  const save = useMutation({
    mutationFn: async () => {
      const geometry = pasted ?? polygonOf(draft);
      if (editing.mode === "new") {
        const { geofence, relinked } = await zoneActions.create({
          name: name.trim(),
          geometry,
          ...(startOff ? { status: "inactive" as const } : {}),
        });
        const after = await allZoneLocations(geofence.id);
        return { id: geofence.id, moved: { name: geofence.name, relinked, ...movedSummary([], after) } };
      }
      const before = await allZoneLocations(editing.zone.id);
      const { geofence, relinked } = await zoneActions.update(editing.zone.id, { name: name.trim(), geometry });
      const after = await allZoneLocations(geofence.id);
      return { id: geofence.id, moved: { name: geofence.name, relinked, ...movedSummary(before, after) } };
    },
    onSuccess: async (result) => {
      await queryClient.invalidateQueries({ queryKey: ["geofences"] });
      onSaved(result);
    },
  });
  return (
    <section className={styles.card} aria-label={editing.mode === "new" ? "New zone" : "Edit the shape"}>
      <h2 className={styles.title}>{editing.mode === "new" ? "New zone" : `Reshape ${editing.zone.name}`}</h2>
      <p className={styles.muted}>
        Click the map to add corners (at least 3). Saving matches every place to its zone again, and shows who moved.
      </p>
      <Input label="Name" value={name} onChange={(e) => setName(e.target.value)} placeholder="Kilimani" />
      <p className={styles.muted}>{draft.length === 1 ? "1 corner" : `${draft.length} corners`}</p>
      {editing.mode === "new" ? (
        <Switch
          label="Start it switched off: check it on the map before places move into it"
          checked={startOff}
          onCheckedChange={setStartOff}
        />
      ) : null}
      <details className={styles.details}>
        <summary>Or paste GeoJSON</summary>
        <Textarea
          label="GeoJSON"
          rows={5}
          value={geojson}
          onChange={(e) => setGeojson(e.target.value)}
          description="A Polygon or MultiPolygon (or a Feature with one), from a GIS tool. It replaces what's drawn, holes and parts included."
          error={geojson.trim() && !pasted ? "Not a Polygon or MultiPolygon." : undefined}
        />
      </details>
      {save.error ? (
        <Alert tone="danger" title="Not saved">
          {messageFor(errorOf(save.error))}
        </Alert>
      ) : null}
      <div className={styles.footer}>
        <Button variant="secondary" onClick={onCancel}>
          Cancel
        </Button>
        <Button
          disabled={!name.trim() || (pasted === null && (draft.length < 3 || Boolean(geojson.trim())))}
          loading={save.isPending}
          onClick={() => save.mutate()}
        >
          Save the zone
        </Button>
      </div>
    </section>
  );
}

function RenameDialog({ zone, onClose }: { zone: Zone; onClose: () => void }) {
  const queryClient = useQueryClient();
  const [name, setName] = useState(zone.name);
  const save = useMutation({
    mutationFn: () => zoneActions.update(zone.id, { name: name.trim() }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["geofences"] });
      onClose();
    },
  });
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent title={`Rename ${zone.name}`} description="A new name moves no place.">
        <div className={styles.form}>
          <Input label="Name" value={name} onChange={(e) => setName(e.target.value)} />
          {save.error ? (
            <Alert tone="danger" title="Not saved">
              {messageFor(errorOf(save.error))}
            </Alert>
          ) : null}
          <div className={styles.footer}>
            <Button variant="secondary" onClick={onClose}>
              Cancel
            </Button>
            <Button disabled={!name.trim()} loading={save.isPending} onClick={() => save.mutate()}>
              Save
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

/** Switching a zone off hands its places to the next zone that covers them, or none; on takes them back. */
function StatusDialog({
  zone,
  onMoved,
  onClose,
}: {
  zone: Zone;
  onMoved: (m: NonNullable<Moved>) => void;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const off = zone.status === "active";
  return (
    <ConfirmDialog
      title={off ? `Switch ${zone.name} off?` : `Switch ${zone.name} on?`}
      description={
        off
          ? `Its ${zone.location_count} places move to the next zone that covers them, or to none: those can't be ordered to until it's back on.`
          : "Places inside it move back to it."
      }
      action={off ? "Switch off" : "Switch on"}
      danger={off}
      run={async () => {
        const before = await allZoneLocations(zone.id);
        const { geofence, relinked } = await zoneActions.update(zone.id, { status: off ? "inactive" : "active" });
        const after = await allZoneLocations(zone.id);
        onMoved({
          name: geofence.name,
          verb: off ? "switched off" : "switched on",
          relinked,
          ...movedSummary(before, after),
        });
      }}
      onDone={() => queryClient.invalidateQueries({ queryKey: ["geofences"] })}
      onClose={onClose}
    />
  );
}

/** Delete: the count of places it holds is shown first, then a press and hold. */
export function DeleteDialog({
  zone,
  onDeleted,
  onClose,
}: {
  zone: Pick<Zone, "id" | "name" | "location_count">;
  onDeleted: (moved: NonNullable<Moved>) => void;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const [confirmed, setConfirmed] = useState<boolean | undefined>(undefined);
  const remove = useMutation({
    mutationFn: async () => {
      const before = await allZoneLocations(zone.id);
      const { relinked } = await zoneActions.remove(zone.id);
      return { name: zone.name, verb: "deleted" as const, relinked, movedIn: [], movedOut: before };
    },
    onSuccess: async (moved) => {
      // the zone is gone: take it off the list now, so nothing asks for its places again
      queryClient.setQueryData(zonesListQuery.queryKey, (zones) => zones?.filter((z) => z.id !== zone.id));
      onDeleted(moved);
      queryClient.removeQueries({ queryKey: ["geofences", "locations", zone.id] });
      await queryClient.invalidateQueries({ queryKey: ["geofences", "all"] });
    },
    onError: () => setConfirmed(false),
  });
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent title={`Delete ${zone.name}?`}>
        <DeleteZoneBody
          count={zone.location_count}
          confirmed={confirmed}
          onConfirm={() => {
            setConfirmed(undefined);
            remove.mutate();
          }}
          error={remove.error}
          onClose={onClose}
        />
      </DialogContent>
    </Dialog>
  );
}

/** What Delete says: first how many places the zone holds and where they go, then the hold. */
export function DeleteZoneBody({
  count: n,
  confirmed,
  onConfirm,
  error,
  onClose,
}: {
  count: number;
  confirmed?: boolean;
  onConfirm: () => void;
  error?: unknown;
  onClose: () => void;
}) {
  return (
    <div className={styles.form}>
      <p className={styles.count}>
        It holds {n === 1 ? "1 place" : `${n} places`}.{" "}
        {n
          ? "They move to the next zone that covers them, or to none: a place in no zone can't be priced or ordered to."
          : "No place is in it."}
      </p>
      <HoldToConfirm
        label="Hold to delete the zone"
        confirmedLabel="Deleted"
        icon={<Trash2 size={16} strokeWidth={1.75} />}
        duration={DELETE_HOLD_MS}
        confirmed={confirmed}
        onConfirm={onConfirm}
      />
      {error ? (
        <Alert tone="danger" title="Not deleted">
          {messageFor(errorOf(error))}
        </Alert>
      ) : null}
      <div className={styles.footer}>
        <Button variant="secondary" onClick={onClose}>
          Keep it
        </Button>
      </div>
    </div>
  );
}

/** What the map's colours mean (the design's legend). */
function MapLegend() {
  return (
    <ul className={styles.legend} aria-label="Key">
      <li>
        <span className={styles.keySelected} aria-hidden /> Open zone
      </li>
      <li>
        <span className={styles.keyZone} aria-hidden /> Zone
      </li>
      <li>
        <span className={styles.keyOff} aria-hidden /> Switched off
      </li>
      <li>
        <span className={styles.keyPlace} aria-hidden /> Place
      </li>
      <li>
        <span className={styles.keyOutside} aria-hidden /> Outside every zone
      </li>
    </ul>
  );
}

/**
 * Places no active zone covers: they can't be priced or ordered to, and nothing else warns. Shown
 * whole here so a missing zone is noticed before a customer finds out.
 */
function OutsidePlaces() {
  const outside = useQuery(outsidePlacesQuery);
  if (!outside.data || outside.data.length === 0) return null;
  return (
    <section className={styles.card} aria-label="Places outside every zone">
      <h2 className={styles.title}>
        Outside every zone ({outside.data.length === 50 ? "50 or more" : outside.data.length})
      </h2>
      <p className={styles.muted}>
        These places can't be priced or ordered to. Draw a zone over them, or fix the point.
      </p>
      <ul className={styles.places}>
        {outside.data.map((l) => (
          <li key={l.id}>
            {l.owner ? (
              <RouterLink
                href={`/${l.owner.type === "business" ? "businesses" : "customers"}/${l.owner.id}?tab=places`}
                className={styles.link}
              >
                {l.owner.name}
              </RouterLink>
            ) : null}{" "}
            · {l.label}
          </li>
        ))}
      </ul>
    </section>
  );
}
